/**
 * Composition root for the soft auth-reset flow. Persists `returnTo` to
 * sessionStorage, clears the `wasAuthenticated` latch, surfaces a
 * notification, and soft-navigates to `/signin`.
 *
 * Used by:
 *   - SDK `onUnauthenticated` (401 path) — module-scoped callback wired
 *     to whatever `<AuthResetGate>` is currently mounted.
 *   - Inactivity logout (Phase 4) — direct invocation from the layout.
 *   - Sidebar manual logout — direct invocation; notification is `null`
 *     for `reason: "manual"`.
 *
 * Defensive try/catch falls back to the hard `window.location.href = "/"`
 * if the soft path throws — better to fully reset state than to leave
 * the user stuck in an ambiguous half-signed-out tree.
 *
 * Note: across-logout draft restoration is intentionally out of scope (see
 * #1011). In-session drafts (navigate away and back without signing out)
 * are still handled by `useHeapFormDraft`.
 *
 * @agentic connections
 */

import { writeReturnTo } from "@/app/auth/returnTo";
import { clearLastActivity } from "@/app/auth/lastActivityStorage";
import {
  AUTH_RESET_NOTIFICATIONS,
  type AuthResetReason,
} from "@/app/auth/notifications";

/** Dependencies the reset flow needs from the surrounding React tree.
 *  @agentic connections */
export type AuthResetDeps = {
  navigate: (to: string) => void;
  location: { pathname: string; search: string; hash: string };
  resetSession: () => void;
  notify: (notification: {
    title: string;
    message: string;
    color: string;
    autoClose?: number | false;
  }) => void;
};

/**
 * Run the soft auth-reset. Always finishes by either soft-navigating to
 * `/signin` or, on catastrophic failure, hard-reloading.
 * @agentic connections
 */
export const performAuthReset = (
  reason: AuthResetReason,
  deps: AuthResetDeps,
): void => {
  try {
    writeReturnTo(deps.location);
    clearLastActivity();
    deps.resetSession();
    const notification = AUTH_RESET_NOTIFICATIONS[reason];
    if (notification) deps.notify(notification);
    deps.navigate("/signin");
  } catch (error) {
    // Last-resort fallback. If anything in the soft path threw, dump
    // state by reloading. Prefer this to a half-reset tree.
    console.error(
      "[performAuthReset] soft path failed; falling back to hard reload",
      error,
    );
    if (typeof window !== "undefined") {
      window.location.href = "/";
    }
  }
};

/** Dependencies for a full logout. Extends {@link AuthResetDeps} with the
 *  network session revoke and a loop-safe local-clear fallback.
 *  @agentic connections */
export type LogoutDeps = AuthResetDeps & {
  /** Revokes the Medplum session over the network (`medplum.signOut()`). */
  signOut: () => Promise<void>;
  /** Synchronously clears the local Medplum session (`medplum.clear()`).
   *  Loop-safe fallback invoked when `signOut()` fails or times out. */
  clearSession: () => void;
};

/** Max wait for the network revoke before forcing a local clear.
 *  `MedplumClient.signOut()` POSTs to the logout endpoint *before* clearing
 *  local state; against an already-invalid token that POST can loop on 401
 *  indefinitely (the SDK retries after a no-op refresh), so we cap the wait.
 *  @agentic connections */
export const SIGN_OUT_TIMEOUT_MS = 3000;

/**
 * Full logout: revoke the Medplum session, then run the soft auth-reset.
 * Both the sidebar logout button and the inactivity auto-logout must clear
 * `profile` (via the session revoke) *and* the `wasAuthenticated` latch (via
 * `performAuthReset`). Skipping the revoke leaves the profile loaded, so the
 * auth gate keeps the user signed in and bounces them off `/signin`.
 *
 * The network revoke is time-boxed. If it rejects or exceeds
 * {@link SIGN_OUT_TIMEOUT_MS} (e.g. the SDK's logout POST is stuck in a 401
 * retry loop on an already-invalid token), we force `clearSession()`. That
 * wipes the in-memory refresh/access tokens, so the SDK's next
 * `handleUnauthenticated` takes the `refresh()`-falsy branch and the loop
 * terminates — and no further request carries the dead token. Either way the
 * user lands on the sign-in screen.
 * @agentic connections
 */
export const performLogout = async (
  reason: AuthResetReason,
  deps: LogoutDeps,
): Promise<void> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    const signOutPromise = deps.signOut();
    // The race below may abandon this promise; swallow any later rejection
    // so the forced clear can't surface an unhandled rejection.
    signOutPromise.catch(() => undefined);
    await Promise.race([
      signOutPromise,
      new Promise<never>((_, reject) => {
        timeoutId = setTimeout(
          () => reject(new Error("signOut timed out")),
          SIGN_OUT_TIMEOUT_MS,
        );
      }),
    ]);
  } catch (error) {
    console.error(
      "[performLogout] medplum.signOut failed or timed out; forcing local session clear",
      error,
    );
    try {
      deps.clearSession();
    } catch (clearError) {
      console.error("[performLogout] forced session clear failed", clearError);
    }
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }
  performAuthReset(reason, deps);
};

// ──────────────────────────────────────────────────────────────────────
// Module-scoped callback registry. The SDK's `onUnauthenticated` hook
// is set once at boot inside `initializeMedplumClient`, but the deps it
// needs (navigate, location, resetSession, notify) come from React. We
// bridge the two via a single module-scoped callback that the
// `<AuthResetGate>` component installs on mount.
// ──────────────────────────────────────────────────────────────────────

type AuthResetCallback = (reason: AuthResetReason) => void;

let activeCallback: AuthResetCallback | null = null;

/** Install the active auth-reset callback. Returns an uninstall function.
 *  @agentic connections */
export const setAuthResetCallback = (
  callback: AuthResetCallback,
): (() => void) => {
  activeCallback = callback;
  return () => {
    if (activeCallback === callback) activeCallback = null;
  };
};

/** Read the currently-installed callback. Returns null when no auth tree
 *  is mounted (e.g. SDK fires before React has hydrated). The fallback
 *  hard-reload in `initializeMedplumClient` covers that case.
 *  @agentic connections */
export const getAuthResetCallback = (): AuthResetCallback | null =>
  activeCallback;

/** Test-only escape hatch. @agentic connections */
export const _resetAuthResetCallbackForTesting = (): void => {
  activeCallback = null;
};
